import { useCallback, useEffect, useRef, type RefObject } from 'react';
import { useReactFlow, useStoreApi } from '@xyflow/react';
import { EMPTY_STEP_PLACEHOLDER_NODE_ID, isEmptyStepPlaceholderNode } from '@bliks-io/ui';
import { toastApiError } from '@/api/mutations/toast-api-error';
import type { UndoableEditorActions } from './useUndoableEditorActions';

/**
 * The editor's two "add a step" entry points outside the context menu: the
 * empty-step placeholder (an empty draft's first step) and the toolbar
 * button, which fills the placeholder while it shows.
 */
export function useAddStepHandlers({
  containerRef,
  undoableActions,
  selectedStepId
}: {
  /** The editor root that scopes its keyboard shortcuts (undo, redo). */
  containerRef: RefObject<HTMLElement | null>;
  undoableActions: UndoableEditorActions;
  selectedStepId: string | null;
}) {
  const { screenToFlowPosition, getNode } = useReactFlow();
  const store = useStoreApi();

  const selectedStepIdRef = useRef(selectedStepId);
  selectedStepIdRef.current = selectedStepId;
  const stopWaitingRef = useRef<(() => void) | null>(null);
  useEffect(function stopWaitingOnUnmount() {
    return () => stopWaitingRef.current?.();
  }, []);

  // Select and focus the new step as a click would, once xyflow shows it —
  // unless the user selected or focused something else in the meantime.
  // Returns the call for when the create has settled: a step that is not on
  // the canvas by then (undone first) is not waited for any longer.
  const selectWhenShown = useCallback(
    (stepId: string, selectionAtActivation: string | null): (() => void) => {
      stopWaitingRef.current?.();
      const container = containerRef.current;
      if (!container) return () => {};
      const findNode = () =>
        container.querySelector<HTMLElement>(`.react-flow__node[data-id="${stepId}"]`);
      // xyflow renders a node `visibility: hidden`, so unfocusable, until its
      // measured size has come back through the controlled nodes.
      const isShown = (node: HTMLElement | null): node is HTMLElement =>
        node !== null && node.style.visibility !== 'hidden';
      const select = (node: HTMLElement) => {
        stopWaitingRef.current?.();
        if (selectedStepIdRef.current !== selectionAtActivation) return;
        const keepsEditorFocus = document.activeElement === container;
        store.getState().addSelectedNodes([stepId]);
        if (keepsEditorFocus) node.focus({ preventScroll: true });
      };
      const shown = findNode();
      if (isShown(shown)) {
        select(shown);
        return () => {};
      }
      let createSettled = false;
      const observer = new MutationObserver(() => {
        const node = findNode();
        if (isShown(node)) select(node);
        else if (!node && createSettled) stop();
      });
      const stop = () => {
        observer.disconnect();
        if (stopWaitingRef.current === stop) stopWaitingRef.current = null;
      };
      observer.observe(container, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['style']
      });
      stopWaitingRef.current = stop;
      return () => {
        createSettled = true;
        if (!findNode()) stop();
      };
    },
    [containerRef, store]
  );

  const handleAddFirstStep = useCallback(
    async (startEventId: string, position: { x: number; y: number }) => {
      // The focused placeholder button unmounts as soon as the optimistic step
      // lands, dropping focus to <body> — outside the editor, where undo and
      // Delete no longer reach it.
      containerRef.current?.focus({ preventScroll: true });
      const selectionAtActivation = selectedStepIdRef.current;
      let onCreateSettled = () => {};
      try {
        await undoableActions.addStepFromStart(startEventId, position, (stepId) => {
          onCreateSettled = selectWhenShown(stepId, selectionAtActivation);
        });
        onCreateSettled();
      } catch (error) {
        stopWaitingRef.current?.();
        toastApiError(error, 'errors.unknown');
      }
    },
    [containerRef, undoableActions, selectWhenShown]
  );

  const handleAddStepCenter = useCallback(() => {
    const placeholder = getNode(EMPTY_STEP_PLACEHOLDER_NODE_ID);
    if (isEmptyStepPlaceholderNode(placeholder)) {
      void placeholder.data.onActivate();
      return;
    }
    const center = screenToFlowPosition({
      x: window.innerWidth / 2,
      y: window.innerHeight / 2
    });
    void undoableActions.addStep({ x: center.x, y: center.y });
  }, [undoableActions, screenToFlowPosition, getNode]);

  return { handleAddFirstStep, handleAddStepCenter };
}
